import React, { useEffect, useState } from 'react';
import usePatientInfo from '../hooks/usePatientInfo';
import { computeAgeFromDicomDate } from './viewerDemographics';
import ViewerStudyChips from './ViewerStudyChips';

/** Figma: line left-arrow (reads clearly vs. thin chevron). */
function PatientBackArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M19 12H5M12 19l-7-7 7-7"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type ViewerRevampPatientBarProps = {
  servicesManager: AppTypes.ServicesManager;
  isReturnEnabled: boolean;
  onBack: () => void;
  trailing?: React.ReactNode;
};

/**
 * Patient strip — Figma [106-8077](https://www.figma.com/design/fUSHo5LGxrEfRYspiKqS4t/RIS-Reporting-flow?node-id=106-8077):
 * Same band height as top bar + toolbar (`.viewer-ris-header-band`). White, border #e2e8f0, name #15294B, IDs #0891B2 + body typography (see `.viewer-ris-patient-bar-ids`).
 * Stacks meta + actions on tablet/mobile so trailing CTAs do not crush patient identity.
 */
function ViewerRevampPatientBar({
  servicesManager,
  isReturnEnabled,
  onBack,
  trailing,
}: ViewerRevampPatientBarProps) {
  const { displaySetService } = servicesManager.services;
  const { patientInfo, isMixedPatients } = usePatientInfo();
  const [birthDate, setBirthDate] = useState<string | null>(null);
  const [accession, setAccession] = useState<string | null>(null);
  /** Hindi Azure overlay from ReportPanel (`ris-patient-display-name`) */
  const [displayNameOverlay, setDisplayNameOverlay] = useState<string | null>(null);

  useEffect(() => {
    const onNameOverlay = (event: Event) => {
      const detail = (event as CustomEvent<{ name?: string | null }>).detail;
      const next = detail?.name != null ? String(detail.name).trim() : '';
      setDisplayNameOverlay(next || null);
    };
    window.addEventListener('ris-patient-display-name', onNameOverlay);
    return () => window.removeEventListener('ris-patient-display-name', onNameOverlay);
  }, []);

  useEffect(() => {
    const read = () => {
      const sets = displaySetService.getActiveDisplaySets?.() ?? [];
      const first = sets[0];
      const inst = first?.instances?.[0] || first?.instance;
      setBirthDate(
        inst?.PatientBirthDate != null && inst?.PatientBirthDate !== ''
          ? String(inst.PatientBirthDate)
          : null
      );
      setAccession(
        inst?.AccessionNumber != null && inst?.AccessionNumber !== ''
          ? String(inst.AccessionNumber)
          : null
      );
    };
    read();
    const sub = displaySetService.subscribe(
      displaySetService.EVENTS.DISPLAY_SETS_ADDED,
      () => read()
    );
    return () => sub.unsubscribe();
  }, [displaySetService]);

  const age = computeAgeFromDicomDate(birthDate);
  const sex = patientInfo.PatientSex?.trim();
  const demoParts = [sex && sex.charAt(0).toUpperCase(), age != null ? String(age) : null].filter(
    Boolean
  );
  const demoSuffix = demoParts.length ? ` (${demoParts.join(', ')})` : '';

  const primaryId = patientInfo.PatientID || null;
  const secondaryId = accession && accession !== primaryId ? accession : null;
  const displayName = displayNameOverlay || patientInfo.PatientName || 'Patient';

  return (
    <div className="viewer-ris-patient-bar viewer-ris-header-band flex w-full items-center justify-between gap-x-3 gap-y-2 overflow-hidden bg-white px-3 sm:px-5">
      <div className="viewer-ris-patient-bar__meta flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
        <button
          type="button"
          disabled={!isReturnEnabled}
          onClick={onBack}
          data-cy="return-to-work-list"
          aria-label="Back to worklist"
          title="Back to worklist"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[#0a0a0a] transition-colors hover:bg-[#f5f5f5] hover:text-[#0f91b2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0891B2]/40 disabled:pointer-events-none disabled:text-[#d4d4d4] sm:h-10 sm:w-10"
        >
          <PatientBackArrowIcon />
        </button>
        <p className="viewer-ris-header-typography min-w-0 shrink truncate text-[#15294b]">
          {isMixedPatients ? 'Multiple patients' : `${displayName}${demoSuffix}`}
        </p>
        {!isMixedPatients && (primaryId || secondaryId) ? (
          <div className="viewer-ris-patient-bar-ids flex min-w-0 flex-1 items-center gap-2">
            {primaryId ? <span className="min-w-0 truncate">{primaryId}</span> : null}
            {secondaryId ? (
              <>
                <span
                  className="hidden h-[18px] w-px shrink-0 bg-[#cbd5e1] sm:block"
                  aria-hidden
                />
                <span className="hidden min-w-0 truncate sm:inline">{secondaryId}</span>
              </>
            ) : null}
          </div>
        ) : (
          <div className="min-h-0 min-w-0 flex-1" />
        )}
      </div>
      <ViewerStudyChips servicesManager={servicesManager} />
      {trailing ? (
        <div className="viewer-ris-patient-bar__actions flex h-auto min-h-9 shrink-0 flex-wrap items-center justify-end gap-1.5 sm:h-10 sm:gap-2">
          {trailing}
        </div>
      ) : (
        <div
          className="h-9 w-9 shrink-0 sm:h-10 sm:w-10"
          aria-hidden
        />
      )}
    </div>
  );
}

export default ViewerRevampPatientBar;
